import React, { useEffect, useRef, useState } from 'react';
import { Upload, Download } from 'lucide-react';

interface Props {
  onImport?: (file: File) => void;
  onExport?: () => void;
  importing?: boolean;
  exporting?: boolean;
  /** Set when the backend import endpoint also accepts Excel (.xlsx/.xls), not just CSV. */
  acceptExcel?: boolean;
}

// The import request is a single request/response with no server-reported progress (the file
// upload itself is near-instant; the time is spent server-side parsing/saving rows), so this
// simulates a percentage that eases toward 90% while waiting and snaps to 100% on completion —
// enough to show the user it's actively working on a long-running import, not stuck.
function useSimulatedProgress(active: boolean | undefined): number {
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (active) {
      setProgress(8);
      intervalRef.current = setInterval(() => {
        setProgress((p) => (p >= 90 ? p : p + (90 - p) * 0.12));
      }, 200);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setProgress((p) => (p > 0 ? 100 : 0));
      const resetTimer = setTimeout(() => setProgress(0), 600);
      return () => clearTimeout(resetTimer);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active]);

  return progress;
}

const BulkImportExport: React.FC<Props> = ({ onImport, onExport, importing, exporting, acceptExcel }) => {
  const importProgress = useSimulatedProgress(importing);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !onImport) return;
    const name = file.name.toLowerCase();
    const isCsv = name.endsWith('.csv');
    const isExcel = acceptExcel && (name.endsWith('.xlsx') || name.endsWith('.xls'));
    if (!isCsv && !isExcel) {
      window.alert(
        acceptExcel
          ? 'Please upload a CSV or Excel (.xlsx/.xls) file.'
          : 'Please upload a CSV file.',
      );
      return;
    }
    onImport(file);
  };

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {onImport && (
        <label
          className="nx-btn nx-btn-outline"
          style={{
            gap: 6,
            cursor: importing ? 'default' : 'pointer',
            opacity: importing ? 0.85 : 1,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Upload size={14} />{' '}
          {importing
            ? `Importing… ${Math.round(importProgress)}%`
            : acceptExcel
              ? 'Import CSV/Excel'
              : 'Import CSV'}
          <input
            type="file"
            accept={
              acceptExcel
                ? '.csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
                : '.csv,text/csv'
            }
            style={{ display: 'none' }}
            disabled={importing}
            onChange={handleFileChange}
          />
          {importing && (
            <span
              style={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                height: 2,
                width: `${importProgress}%`,
                background: 'var(--nx-blue)',
                transition: 'width 0.2s linear',
              }}
            />
          )}
        </label>
      )}
      {onExport && (
        <button
          type="button"
          onClick={onExport}
          disabled={exporting}
          className="nx-btn nx-btn-outline"
          style={{ gap: 6, opacity: exporting ? 0.6 : 1 }}
        >
          <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      )}
    </div>
  );
};

export default BulkImportExport;
